/**
 * Five of a kind: the moment a player's five dice match (rolled, or made with a power), every
 * die goes wild for the rest of their turn (`TurnView.wild`, `GameView.lastWild`).
 *
 * - `useWild`: each new `lastWild` plays once (not ones before mount): it waits while anything
 *   else plays out (`blocked`: the roll that made it landing, say), then `splash` is up for
 *   `WILD_SPLASH_MS` (the server holds bot moves as long), then, in Beginner Mode, `lesson`
 *   until `lessonDone`. The server pauses play for Beginner Mode players until they are through.
 * - `WildSplash`: the full-screen '5 of a Kind' / 'All Dice Are Now Wild' splash everyone sees.
 * - `WildLesson`: Beginner Mode: what wild dice mean, with the Kill Quests on the table as examples.
 */
import { useEffect, useState } from 'react';
import { WILD_SPLASH_MS, t, type CardDef, type CardId, type GameView, type WildView } from '@kadice/shared';
import { playSound } from '../audio';
import { killExample } from '../beginner';
import { anim } from '../speed';
import { tx } from '../text';
import { Dice } from './Dice';
import { PName } from './Names';

interface WildFx {
  seen: number;
  /** Seen, waiting for the table to be free. */
  pending: WildView | null;
  splash: WildView | null;
  lesson: WildView | null;
}

export function useWild(
  view: GameView,
  beginner: boolean,
  blocked: boolean,
): { splash: WildView | null; lesson: WildView | null; waiting: boolean; lessonDone: () => void } {
  const wild = view.lastWild;
  const seq = wild?.seq ?? 0;
  const [state, setState] = useState<WildFx>({ seen: seq, pending: null, splash: null, lesson: null });

  // Used in this same render (as the reveal hooks do), so nothing downstream sees a gap.
  let current = state;
  if (seq > current.seen) current = { ...current, seen: seq, pending: wild };
  if (current.pending && !blocked && !current.splash) current = { ...current, pending: null, splash: current.pending };
  if (current !== state) setState(current);

  const splash = current.splash;
  useEffect(() => {
    if (!splash) return;
    const done = setTimeout(
      () => setState((s) => (s.splash === splash ? { ...s, splash: null, lesson: beginner ? splash : null } : s)),
      anim(WILD_SPLASH_MS),
    );
    return () => clearTimeout(done);
  }, [splash, beginner]);

  return {
    splash,
    lesson: beginner && !splash ? current.lesson : null,
    waiting: current.pending !== null,
    lessonDone: () => setState((s) => ({ ...s, lesson: null })),
  };
}

/**
 * Ka'Dice: each new `lastKadice` gets the same two-pane splash (sound with it), once it is the
 * table's turn (`blocked`: the roll landing, an attack, five of a kind's splash). No lesson.
 */
export function useKadice(view: GameView, blocked: boolean): WildView | null {
  const kadice = view.lastKadice;
  const seq = kadice?.seq ?? 0;
  const [state, setState] = useState<{ seen: number; pending: WildView | null; splash: WildView | null }>({
    seen: seq,
    pending: null,
    splash: null,
  });
  let current = state;
  if (seq > current.seen) current = { ...current, seen: seq, pending: kadice };
  if (current.pending && !blocked && !current.splash) current = { ...current, pending: null, splash: current.pending };
  if (current !== state) setState(current);

  const splash = current.splash;
  useEffect(() => {
    if (!splash) return;
    playSound('kadicePower');
    const done = setTimeout(
      () => setState((s) => (s.splash === splash ? { ...s, splash: null } : s)),
      anim(WILD_SPLASH_MS),
    );
    return () => clearTimeout(done);
  }, [splash]);
  return splash;
}

const nameOf = (view: GameView, wild: WildView) =>
  view.players.find((p) => p.id === wild.playerId)?.name ?? t('player.someone');

export function WildSplash({ view, wild }: { view: GameView; wild: WildView }) {
  return (
    <div className="wild-splash" role="alert" style={{ animationDuration: `${anim(WILD_SPLASH_MS)}ms` }}>
      <div className="wild-half wild-top">
        <span className="wild-band wild-title">{t('wild.title')}</span>
      </div>
      <div className="wild-half wild-bottom">
        <span className="wild-band wild-sub">{t('wild.subtitle')}</span>
        <p className="wild-by">{tx('wild.by', { name: <PName>{nameOf(view, wild)}</PName>, face: wild.dice[0] })}</p>
      </div>
    </div>
  );
}

/** Ka'Dice: 'Ka' slams in on the top half, 'Dice!' on the bottom (same look as `WildSplash`). */
export function KadiceSplash({ view, kadice }: { view: GameView; kadice: WildView }) {
  return (
    <div className="wild-splash" role="alert" style={{ animationDuration: `${anim(WILD_SPLASH_MS)}ms` }}>
      <div className="wild-half wild-top">
        <span className="wild-band wild-title">{t('splash.kadiceTop')}</span>
      </div>
      <div className="wild-half wild-bottom">
        <span className="wild-band wild-sub">{t('splash.kadiceBottom')}</span>
        <p className="wild-by">{tx('splash.kadiceBy', { name: <PName>{nameOf(view, kadice)}</PName> })}</p>
      </div>
    </div>
  );
}

/** Fallback examples when the table has no other Kill Quests to show. */
const GENERIC_QUESTS = ['seq5', 'xxxyy', 'kadice'];

export function WildLesson({
  view,
  wild,
  catalog,
  onDone,
}: {
  view: GameView;
  wild: WildView;
  catalog: ReadonlyMap<CardId, CardDef>;
  onDone: () => void;
}) {
  const mine = wild.playerId === view.you.id;
  // Examples: the Kill Quests of the cards the wild player could attack, else a few classics.
  const onTable = view.players
    .filter((p) => p.id !== wild.playerId && p.card !== null && p.revealed)
    .map((p) => catalog.get(p.card!)?.killer)
    .filter((k): k is string => !!k && !!killExample(k));
  const quests = [...new Set(onTable.length > 0 ? onTable : GENERIC_QUESTS)].slice(0, 3);
  const vars = { name: <PName>{nameOf(view, wild)}</PName>, face: wild.dice[0] };

  return (
    <div className="modal-backdrop">
      <div className="modal splash wild-lesson" role="dialog" aria-modal="true">
        <h2>{t('wildLesson.title')}</h2>
        <Dice values={wild.dice} />
        <p>{mine ? tx('wildLesson.bodyYou', vars) : tx('wildLesson.bodyOther', vars)}</p>
        <p className="muted">{t('wildLesson.examples')}</p>
        <ul className="wild-examples">
          {quests.map((q, i) => (
            <li key={q} style={{ animationDelay: `${i * 0.4}s` }}>
              <Dice values={killExample(q)!.dice} small lifted={killExample(q)!.lit} />
              <span>{t('wildLesson.example', { quest: t(`killer.${q}`) })}</span>
            </li>
          ))}
        </ul>
        <p className="muted small">{t('wildLesson.how')}</p>
        {mine && <p>{t('wildLesson.keepYou')}</p>}
        <div className="row actions">
          <button className="primary intro-next" onClick={onDone}>
            {t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
